import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { vehicleEquipmentSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { VehicleEquipment } from './model'
import { Vehicle } from '../vehicles/model'
import * as vehiclesActionCreators from '../vehicles/actionCreators'
import { Equipment } from '../equipments/model'
import * as equipmentsActionCreators from '../equipments/actionCreators'

// Fetch

const fetchVehicleEquipmentsEpic = (action$, store) => {
    const tableIdentifier = 'vehicle_equipments_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `vehicle-equipments/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(vehicleEquipmentSchema)
                        )

                        const vehicleEquipments = normalizedEntitiesToRecordMap(
                            normalizedData.entities.vehicle_equipments,
                            VehicleEquipment,
                            normalizedData.result
                        )
                        const vehicles = normalizedEntitiesToRecordMap(normalizedData.entities.vehicles, Vehicle)
                        const equipments = normalizedEntitiesToRecordMap(normalizedData.entities.equipments, Equipment)

                        const observables = [
                            Observable.of(vehiclesActionCreators.fetchVehiclesFulfilled(vehicles)),
                            Observable.of(equipmentsActionCreators.fetchEquipmentsFulfilled(equipments)),
                            Observable.of(actionCreators.fetchVehicleEquipmentsFulfilled(vehicleEquipments)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchVehicleEquipmentsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchVehicleEquipmentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().vehicleEquipments.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `vehicle-equipments/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, vehicleEquipmentSchema)

                        const vehicleEquipments = normalizedEntitiesToRecordMap(normalizedData.entities.vehicle_equipments, VehicleEquipment)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchVehicleEquipmentsFulfilled(vehicleEquipments)),
                            Observable.of(actionCreators.fetchVehicleEquipmentFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchVehicleEquipmentsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveVehicleEquipmentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().vehicleEquipments.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload.values)
            if (action.payload.ignoreDuplicity) {
                values.ignore_duplicity = 1
            }

            let path = `vehicle-equipments`
            let method = 'POST'

            if (values.id) {
                path = `vehicle-equipments/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        if (ajaxResponse.response.duplicity_found) {
                            return Observable.concat(
                                Observable.of(actionCreators.saveVehicleEquipmentDuplicityFound(ajaxResponse.response.duplicity))
                            )
                        }

                        const normalizedData = normalize(ajaxResponse.response, vehicleEquipmentSchema)

                        const vehicleEquipments = normalizedEntitiesToRecordMap(normalizedData.entities.vehicle_equipments, VehicleEquipment)
                        const vehicleEquipment = vehicleEquipments.valueSeq().first()

                        return Observable.of(actionCreators.saveVehicleEquipmentFulfilled(vehicleEquipment))
                    })
                    .catch(error => Observable.of(actionCreators.saveVehicleEquipmentRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteVehicleEquipmentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().vehicleEquipments.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `vehicle-equipments/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteVehicleEquipmentFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteVehicleEquipmentRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportVehicleEquipmentsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `vehicle-equipments/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [
    fetchVehicleEquipmentsEpic,
    fetchVehicleEquipmentEpic,
    saveVehicleEquipmentEpic,
    deleteVehicleEquipmentEpic,
    exportVehicleEquipmentsEpic,
]
